import { createSelector } from "@reduxjs/toolkit";

import { selectCurrentUser } from "../global";

import { selectUserIdsWithCapability } from "./capability";
import { selectIsVideoResourcePlayingForUser } from "./resource";

import { CapabilityType, RootState } from "shared/types";
import { BandwidthModeType, RoleType } from "shared/types/user";
import {
  getCurrentlyPresentUserCount,
  getNonObserverUserCount,
  getNonObserverUsers,
} from "shared/utils/monitoring";

export const selectCurrentUserIsVisible = () =>
  createSelector(
    [selectCurrentUser(), selectUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS)],
    (currentUser, isVisibleInClassUserIds) => {
      const userId = currentUser?.id;
      return userId && isVisibleInClassUserIds.includes(userId);
    },
  );

export const selectCurrentUserIsRecordingAgent = () =>
  createSelector([selectCurrentUser()], (currentUser) => {
    return currentUser?.is_recording_agent;
  });

export const selectRoleForCurrentUser = () =>
  createSelector([selectCurrentUser()], (currentUser) => {
    return currentUser?.role;
  });

export const selectUsersWithRole = (role: RoleType) => (state: RootState) =>
  Object.values(state.classroom.client.users).filter((user) => user.role === role);

export const selectTeacher = () => (state: RootState) => {
  const teachers = selectUsersWithRole(RoleType.Teacher)(state);
  return teachers.length > 0 ? teachers[0] : null;
};

export const selectUserIdForTeacher = () =>
  createSelector([selectUsersWithRole(RoleType.Teacher)], (teacherUsers) => {
    return teacherUsers[0]?.id;
  });

const _selectUserById = (userId: string) => (state: RootState) =>
  userId in state.classroom.client.users ? state.classroom.client.users[userId] : null;

export const selectNonObserverCount = () => (state: RootState) =>
  getNonObserverUserCount(state.classroom.client);

export const selectCurrentlyPresentUserCount = () => (state: RootState) =>
  getCurrentlyPresentUserCount(state.classroom.client);

export const selectUserHasClassroomInFocus = (userId: string) => (state: RootState) =>
  !!state.classroom.client.users[userId]?.has_classroom_in_focus;

export const selectBandwidthModes = () => (state: RootState) => {
  const users = getNonObserverUsers(state.classroom.client);
  const bandwidthModes = users.reduce(
    (acc, user) => {
      const newAcc = {
        [user.bandwidth_mode]: acc[user.bandwidth_mode] + 1,
      };
      return { ...acc, ...newAcc };
    },
    {
      [BandwidthModeType.AutomaticBandwidth]: 0,
      [BandwidthModeType.LimitedBandwidth]: 0,
      [BandwidthModeType.AudioOnly]: 0,
      [BandwidthModeType.AudioOnlyForced]: 0,
    },
  );
  return `${bandwidthModes[BandwidthModeType.AutomaticBandwidth]}-${
    bandwidthModes[BandwidthModeType.LimitedBandwidth]
  }-${bandwidthModes[BandwidthModeType.AudioOnly]}-${
    bandwidthModes[BandwidthModeType.AudioOnlyForced]
  }`;
};

export const selectCameras = () => (state: RootState) => {
  const users = getNonObserverUsers(state.classroom.client);
  const cameras = users.reduce(
    (acc, user) => {
      const key = user.av.camera_error ? "disabled" : "enabled";
      const newAcc = {
        [key]: acc[key] + 1,
      };
      return { ...acc, ...newAcc };
    },
    {
      enabled: 0,
      disabled: 0,
    },
  );
  return `${cameras.enabled}-${cameras.disabled}`;
};

export const selectForcedPttMode = selectIsVideoResourcePlayingForUser;

export const selectEffectivePttMode = (userId: string) =>
  createSelector(
    [_selectUserById(userId), selectIsVideoResourcePlayingForUser(userId)],
    (currentUser, isVideoPlaying) => isVideoPlaying || currentUser?.ptt_mode || false,
  );
